import { mergeClasses } from "@fluentui/react-components";
import {
  AddRegular,
  CheckmarkCircleRegular,
  DeleteRegular,
  EditRegular,
  SparkleRegular,
} from "@fluentui/react-icons";
import {
  deleteDeliverable,
  getDeliverables,
  updateDeliverable,
} from "../../api";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { AdminListPage } from "components/adminList/AdminListPage";
import { IAdminRowAction } from "components/adminList/AdminListPage.types";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";
import { useAdminListPagination } from "hooks/useAdminListPagination";
import { useAdminListSearch } from "hooks/useAdminListSearch";
import { useAdminListSort } from "hooks/useAdminListSort";
import { IDeliverable } from "models/IProjectIQWorkspace";
import { ITableColumn } from "models/ITableColumn";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import {
  selectAvailableProjectIqWorkspaces,
  updateProjectIqWorkspace as updateWorkspaceAction,
} from "redux/projectIqWorkspacesSlice";
import { formatDate } from "utils/generalUtils";
import { useStyles } from "./DeliverablesPage.styles";
import {
  DELIVERABLES_QUERY_KEY,
  IDeliverablesPageProps,
  STATUS_LABELS,
} from "./DeliverablesPage.types";
import { DeliverableFormDialog } from "./DeliverableFormDialog";

const ACTIONS_COLUMN_WIDTH_NARROW = "96px";
const ACTIONS_COLUMN_WIDTH_WIDE = "150px";

export const DeliverablesPage = ({
  workspaceId,
  onDeliverableSaved,
}: IDeliverablesPageProps): JSX.Element => {
  const styles = useStyles();
  const colStyles = useAdminColumnStyles();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const workspaces = useSelector(selectAvailableProjectIqWorkspaces);
  const workspace = workspaces.find((w) => w.id === workspaceId) ?? null;

  const [dialogState, setDialogState] = useState<{
    isOpen: boolean;
    deliverable: IDeliverable | null;
  }>({ isOpen: false, deliverable: null });
  const [deletingDeliverable, setDeletingDeliverable] =
    useState<IDeliverable | null>(null);

  const {
    data: deliverables,
    isLoading: isDeliverablesLoading,
    isFetching: isDeliverablesFetching,
  } = useQuery<IDeliverable[], Error>(
    [DELIVERABLES_QUERY_KEY, workspaceId],
    () => getDeliverables(workspaceId),
    { enabled: !!workspaceId },
  );

  useEffect(() => {
    if (workspace?.sowAnalysis?.status === "completed") {
      queryClient.invalidateQueries([DELIVERABLES_QUERY_KEY, workspaceId]);
    }
  }, [workspace?.sowAnalysis?.completedAt, workspaceId, queryClient]);

  const deleteMutation = useMutation(
    (deliverableId: string) => deleteDeliverable(workspaceId, deliverableId),
    {
      onSuccess: (_, deliverableId) => {
        queryClient.invalidateQueries([DELIVERABLES_QUERY_KEY, workspaceId]);
        if (workspace) {
          dispatch(
            updateWorkspaceAction({
              ...workspace,
              deliverables: (workspace.deliverables ?? []).filter(
                (d) => d.id !== deliverableId,
              ),
            }),
          );
        }
        setDeletingDeliverable(null);
        onDeliverableSaved?.();
      },
    },
  );

  const confirmKeepMutation = useMutation(
    (deliverable: IDeliverable) =>
      updateDeliverable(workspaceId, deliverable.id, {
        isDraftedFromSow: false,
      }),
    {
      onSuccess: (updated) => {
        queryClient.invalidateQueries([DELIVERABLES_QUERY_KEY, workspaceId]);
        if (workspace) {
          dispatch(
            updateWorkspaceAction({
              ...workspace,
              deliverables: (workspace.deliverables ?? []).map((d) =>
                d.id === updated.id ? updated : d,
              ),
            }),
          );
        }
      },
    },
  );

  const handleOpenCreate = useCallback(() => {
    setDialogState({ isOpen: true, deliverable: null });
  }, []);

  const handleOpenEdit = useCallback((deliverable: IDeliverable) => {
    setDialogState({ isOpen: true, deliverable });
  }, []);

  const handleDialogDismiss = useCallback(
    (saved: boolean) => {
      setDialogState({ isOpen: false, deliverable: null });
      if (saved) onDeliverableSaved?.();
    },
    [onDeliverableSaved],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (deletingDeliverable) {
      await deleteMutation.mutateAsync(deletingDeliverable.id);
    }
  }, [deletingDeliverable, deleteMutation]);

  const handleConfirmKeep = useCallback(
    (deliverable: IDeliverable) => {
      confirmKeepMutation.mutate(deliverable);
    },
    [confirmKeepMutation],
  );

  const pillClass = useMemo(
    () => ({
      done: mergeClasses(styles.pill, styles.pillDone),
      in_progress: mergeClasses(styles.pill, styles.pillInProgress),
      not_started: mergeClasses(styles.pill, styles.pillNotStarted),
    }),
    [styles],
  );

  const searchFields = useMemo(
    () =>
      ["name", "description", "owner", "reviewer"] as (keyof IDeliverable)[],
    [],
  );

  const { searchQuery, handleSearchChange, filteredItems } =
    useAdminListSearch<IDeliverable>({
      items: deliverables ?? [],
      searchFields,
    });

  const {
    sortColumn,
    isSortDescending,
    sortItems,
    onSortColumnChanged,
    onClearColumnChanged,
  } = useAdminListSort<IDeliverable>({
    cacheKey: "Deliverables",
    defaultSortColumn: "name",
    defaultDescending: false,
  });

  const columns: ITableColumn<IDeliverable>[] = useMemo(
    () => [
      {
        columnId: "name",
        displayValue: "Name",
        className: colStyles.wide,
        renderHeaderCell: () => "Name",
        compare: (a: IDeliverable, b: IDeliverable) =>
          a.name.localeCompare(b.name),
        renderCell: (item: IDeliverable) => item.name,
      },
      {
        columnId: "description",
        displayValue: "Description",
        className: colStyles.wide,
        renderHeaderCell: () => "Description",
        compare: (a: IDeliverable, b: IDeliverable) =>
          (a.description ?? "").localeCompare(b.description ?? ""),
        renderCell: (item: IDeliverable) => item.description ?? "—",
      },
      {
        columnId: "dueDate",
        displayValue: "Due Date",
        className: colStyles.normal,
        renderHeaderCell: () => "Due Date",
        compare: (a: IDeliverable, b: IDeliverable) =>
          (a.dueDate ?? "").localeCompare(b.dueDate ?? ""),
        renderCell: (item: IDeliverable) =>
          formatDate(
            item.dueDate
              ? Math.floor(
                  new Date(item.dueDate + "T00:00:00").getTime() / 1000,
                )
              : undefined,
            "—",
          ),
      },
      {
        columnId: "status",
        displayValue: "Status",
        className: colStyles.normal,
        renderHeaderCell: () => "Status",
        compare: (a: IDeliverable, b: IDeliverable) =>
          a.status.localeCompare(b.status),
        renderCell: (item: IDeliverable) => (
          <span className={pillClass[item.status]}>
            {STATUS_LABELS[item.status]}
          </span>
        ),
      },
      {
        columnId: "owner",
        displayValue: "Owner",
        className: colStyles.normal,
        renderHeaderCell: () => "Owner",
        compare: (a: IDeliverable, b: IDeliverable) =>
          (a.owner ?? "").localeCompare(b.owner ?? ""),
        renderCell: (item: IDeliverable) => item.owner ?? "—",
      },
      {
        columnId: "reviewer",
        displayValue: "Reviewer",
        className: colStyles.normal,
        renderHeaderCell: () => "Reviewer",
        compare: (a: IDeliverable, b: IDeliverable) =>
          (a.reviewer ?? "").localeCompare(b.reviewer ?? ""),
        renderCell: (item: IDeliverable) => item.reviewer ?? "—",
      },
    ],
    [colStyles, pillClass],
  );

  const sortedItems = useMemo(
    () => sortItems(filteredItems, columns),
    [filteredItems, sortItems, columns],
  );

  const {
    paginatedItems,
    selectedPageIndex,
    pageCount,
    handlePageChange,
    totalItemsCount,
    itemsPerPage,
  } = useAdminListPagination<IDeliverable>({
    items: sortedItems,
    itemsPerPage: 15,
  });

  const hasSowDraftedDeliverables = useMemo(
    () => (deliverables ?? []).some((d) => d.isDraftedFromSow),
    [deliverables],
  );
  const actionsWidth = hasSowDraftedDeliverables
    ? ACTIONS_COLUMN_WIDTH_WIDE
    : ACTIONS_COLUMN_WIDTH_NARROW;

  const rowActions: IAdminRowAction<IDeliverable>[] = useMemo(
    () => [
      ...(hasSowDraftedDeliverables
        ? [
            {
              icon: <SparkleRegular />,
              tooltip: "Drafted from SOW",
              isNonInteractive: true,
              isVisible: (item: IDeliverable) => !!item.isDraftedFromSow,
            },
            {
              icon: <CheckmarkCircleRegular />,
              tooltip: "Confirm and keep",
              onClick: handleConfirmKeep,
              isVisible: (item: IDeliverable) => !!item.isDraftedFromSow,
            },
          ]
        : []),
      {
        icon: <EditRegular />,
        tooltip: "Edit",
        onClick: handleOpenEdit,
      },
      {
        icon: <DeleteRegular />,
        tooltip: "Delete",
        onClick: setDeletingDeliverable,
      },
    ],
    [hasSowDraftedDeliverables, handleOpenEdit, handleConfirmKeep],
  );

  return (
    <>
      <AdminListPage<IDeliverable>
        items={deliverables ?? []}
        columns={columns}
        noPadding
        isLoading={isDeliverablesLoading || isDeliverablesFetching}
        searchPlaceholder="Search deliverables..."
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        rowActions={rowActions}
        headerActions={[
          {
            label: "Add New",
            icon: <AddRegular aria-hidden="true" />,
            onClick: handleOpenCreate,
            appearance: "primary",
          },
        ]}
        getRowId={(item) => item.id}
        sortColumn={sortColumn}
        isSortDescending={isSortDescending}
        onSortChange={onSortColumnChanged}
        onSortClear={onClearColumnChanged}
        paginatedItems={paginatedItems}
        selectedPageIndex={selectedPageIndex}
        pageCount={pageCount}
        totalItemsCount={totalItemsCount}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        emptyStateMessage={
          searchQuery
            ? "No deliverables match the search."
            : "No deliverables yet."
        }
        actionsWidth={actionsWidth}
      />

      <DeliverableFormDialog
        isOpen={dialogState.isOpen}
        deliverable={dialogState.deliverable}
        workspaceId={workspaceId}
        onDismiss={handleDialogDismiss}
      />

      <ActionDialog
        isVisible={deletingDeliverable !== null}
        title="Delete Deliverable"
        message={`Are you sure you want to delete "${deletingDeliverable?.name}"? This action cannot be undone.`}
        confirmationMessage="Delete"
        dismissMessage="Cancel"
        onConfirmation={handleConfirmDelete}
        onDismiss={() => setDeletingDeliverable(null)}
      />
    </>
  );
};
